import { useState } from "react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import FadeIn from "../components/FadeIn";
import SEO from "../components/SEO";

const STORY_TABS = [
  {
    id: "idea",
    label: "Idea",
    title: "The idea behind HAEVN",
    callout: null,
    blocks: [
      "For the last decade, online dating has mostly meant one thing: swiping. Right. Left. Repeat.",
      "The problem is simple. Swiping rewards attention, not alignment. The loudest profiles rise. The best photos win. People chase matches instead of finding real compatibility.",
      "HAEVN was built to take a different approach.",
      "Instead of asking people to perform for attention, it asks a different question: what kind of connection are you actually looking for?",
      "When you answer that clearly, everything changes.",
    ],
  },
  {
    id: "matching",
    label: "Matching",
    title: "A different way to match",
    callout: "80%+ compatibility before you ever connect.",
    blocks: [
      "HAEVN isn't designed like a traditional dating app. There's no endless browsing. No swiping. No pressure to constantly check in.",
      "You complete a detailed survey that defines who you are and what you want. From there, the network continuously looks for people who align with you.",
      "When two people reach 80% compatibility or higher, they become a match. Matches are revealed weekly so both people are notified at the same time.",
      "You go live your life. The network does the searching.",
    ],
  },
  {
    id: "intentions",
    label: "Intentions",
    title: "Built for real intentions",
    callout: null,
    blocks: [
      "People come to HAEVN looking for different kinds of connection.",
      "Some want a long-term relationship. Some are exploring something casual. Others are part of open or non-traditional relationship structures.",
      "What they share is clarity.",
      "They know what they want, and they're honest about it.",
      "When intent is clear, compatibility becomes easier to find.",
    ],
  },
  {
    id: "trust",
    label: "Trust",
    title: "Privacy and trust",
    callout: "Verified profiles. Protected information.",
    blocks: [
      "Meaningful connection requires trust.",
      "Profiles are verified. Personal information is protected. Matches are only revealed when alignment is high.",
      "You're not broadcasting yourself to the entire internet. You're connecting within a network designed around intent.",
    ],
  },
  {
    id: "vision",
    label: "Vision",
    title: "The bigger vision",
    callout: "Less noise. More signal.",
    blocks: [
      "HAEVN isn't just another dating product.",
      "The goal is to build a matching network for modern relationships. A system where people define what they want and are introduced to others who want the same thing.",
      "Less noise. More signal.",
    ],
  },
];

const HERO_IMG = "https://customer-assets.emergentagent.com/job_2ce4513b-5c90-4107-90a4-2290c44a379e/artifacts/e9b3l1zh_u2427732176_Candid_behind_the_scenes_look_at_an_app_developme_42507912-75d5-4a15-9e24-9c515ac78e8d_3%281%29.png";
const STATEMENT_IMG = "https://images.unsplash.com/photo-1748137777467-80f58c230938?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxjb3VwbGUlMjB3YWxraW5nJTIwY2l0eSUyMG5pZ2h0JTIwbGlnaHRzJTIwZWRpdG9yaWFsfGVufDB8fHx8MTc3NDg4Mzk5NXww&ixlib=rb-4.1.0&q=85&w=800";

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState("idea");
  const content = STORY_TABS.find((t) => t.id === activeTab);

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <SEO
        title="About"
        path="/about"
        description="HAEVN is a matching network built on the belief that connection should start with alignment, not attraction. Learn our story."
      />
      <Navigation />

      {/* 1. HERO — Split editorial layout */}
      <section className="pt-28 pb-16 md:pb-24 px-6 md:px-12" data-testid="about-hero">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <div>
            <FadeIn>
              <p className="text-xs font-subhead uppercase tracking-widest text-gray-400 mb-4">About HAEVN</p>
              <h1 className="font-headline text-4xl sm:text-5xl lg:text-[3.5rem] text-haevn-navy leading-[1.08] mb-6">
                We're building a better system for connection.
              </h1>
              <p className="text-lg text-gray-500 font-body leading-relaxed max-w-md">
                A matching network for modern relationships, built around clarity, alignment, and real intent.
              </p>
            </FadeIn>
          </div>
          <div className="relative">
            <FadeIn delay={200}>
              <div className="rounded-2xl overflow-hidden aspect-[4/5] md:aspect-[3/4]">
                <img
                  src={HERO_IMG}
                  alt="People connecting"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 w-28 h-28 md:w-36 md:h-36 rounded-xl bg-haevn-navy/5 -z-10" />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* 2. MISSION — Featured statement block */}
      <section className="px-6 md:px-12 py-20 md:py-28 bg-[#F3F2F0]" data-testid="about-mission">
        <div className="max-w-5xl mx-auto">
          <div className="flex gap-8">
            <div className="hidden md:block w-px bg-haevn-teal/30 shrink-0 self-stretch" />
            <div>
              <FadeIn>
                <p className="text-xs font-subhead uppercase tracking-[0.08em] text-gray-400 mb-6">Mission</p>
                <h2 className="font-headline text-3xl md:text-[3rem] text-gray-900 leading-[1.1] mb-8" style={{ fontWeight: 500 }}>
                  HAEVN exists to build a better system for connection.
                </h2>
              </FadeIn>
              <FadeIn delay={100}>
                <div className="max-w-[720px] space-y-5 mb-8">
                  <p className="text-lg text-gray-600 font-body leading-relaxed">
                    One built around clarity, alignment, and real intent instead of endless browsing and noise.
                  </p>
                  <p className="text-lg text-gray-600 font-body leading-relaxed">
                    A system where people define what they want and are introduced to others who want the same thing.
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-lg text-gray-800 font-body leading-relaxed" style={{ fontWeight: 500 }}>
                    Less performance. More honesty.
                  </p>
                  <p className="text-lg text-gray-800 font-body leading-relaxed" style={{ fontWeight: 500 }}>
                    Less noise. More signal.
                  </p>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* 3. STATEMENT / IMAGE SPLIT */}
      <section className="px-6 md:px-12 py-20 md:py-28" data-testid="about-statement">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          <FadeIn>
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src={STATEMENT_IMG}
                alt="Connection"
                className="w-full h-full object-cover"
              />
            </div>
          </FadeIn>
          <FadeIn delay={150}>
            <div>
              <h2 className="font-headline text-3xl md:text-4xl text-haevn-navy leading-[1.1] mb-5">
                You don't search profiles.<br />
                The network continuously finds your 80%+ matches.
              </h2>
              <p className="text-lg text-gray-500 font-body leading-relaxed">
                That's what changes when connection is built around alignment instead of attention.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 4. INTERACTIVE STORY RAIL — Vertical nav + content panel */}
      <section className="px-6 md:px-12 py-20 md:py-28 bg-[#F3F2F0]" data-testid="about-story-rail">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <p className="text-xs font-subhead uppercase tracking-widest text-gray-400 mb-10">The HAEVN System</p>
            <div className="mb-12">
              <p className="font-headline text-2xl md:text-3xl text-gray-900 leading-tight">
                Most platforms are built around attention.<br />
                <span className="text-haevn-teal">HAEVN is built around alignment.</span>
              </p>
            </div>
          </FadeIn>

          {/* Mobile: horizontal scrollable tabs */}
          <div className="md:hidden mb-8">
            <nav
              className="flex gap-6 overflow-x-auto pb-3 border-b border-gray-300"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              data-testid="about-tabs-mobile"
            >
              <style>{`[data-testid="about-tabs-mobile"]::-webkit-scrollbar { display: none; }`}</style>
              {STORY_TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`text-base font-body whitespace-nowrap pb-2 cursor-pointer transition-colors duration-200 border-b-2 ${
                    activeTab === tab.id
                      ? "text-gray-900 border-gray-900"
                      : "text-gray-400 border-transparent"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Desktop: two-column layout */}
          <div className="md:grid md:grid-cols-[200px_1fr] md:gap-16">
            {/* Left vertical nav (desktop only) */}
            <nav className="hidden md:flex flex-col gap-1" data-testid="about-tabs-desktop">
              {STORY_TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`text-left py-3 px-4 text-base font-body cursor-pointer transition-all duration-200 rounded-lg border-l-2 ${
                    activeTab === tab.id
                      ? "text-gray-900 bg-white border-haevn-navy shadow-sm"
                      : "text-gray-400 border-transparent hover:text-gray-600 hover:bg-white/50"
                  }`}
                  style={{ fontWeight: activeTab === tab.id ? 500 : 400 }}
                  data-testid={`about-tab-${tab.id}`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>

            {/* Right content panel */}
            <div
              key={activeTab}
              className="bg-white rounded-2xl p-8 md:p-10 shadow-sm"
              style={{ animation: "aboutPanelIn 250ms ease forwards" }}
              data-testid="about-content-panel"
            >
              <h3 className="font-headline text-2xl md:text-3xl text-gray-900 mb-6 leading-tight">
                {content.title}
              </h3>
              {content.callout && (
                <div className="mb-6 pl-4 border-l-2 border-haevn-teal">
                  <p className="text-sm font-subhead text-haevn-teal">{content.callout}</p>
                </div>
              )}
              <div className="space-y-4">
                {content.blocks.map((block, i) => (
                  <p key={i} className="text-base text-gray-600 font-body leading-relaxed">
                    {block}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. FOUNDER — Portrait + Pull quote + story */}
      <section className="px-6 md:px-12 py-20 md:py-28" data-testid="about-founder">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <p className="text-xs font-subhead uppercase tracking-widest text-gray-400 mb-8">Founder</p>
          </FadeIn>

          <div className="grid md:grid-cols-[280px_1fr] gap-10 md:gap-16 items-start">
            <FadeIn delay={100}>
              <div className="rounded-2xl overflow-hidden aspect-[3/4] w-full max-w-[280px]">
                <img
                  src="https://customer-assets.emergentagent.com/job_2ce4513b-5c90-4107-90a4-2290c44a379e/artifacts/oi8q8ktx_20251205_202027455_iOS.jpg"
                  alt="Rik Foote, Founder of HAEVN"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </FadeIn>

            <div>
              <FadeIn delay={150}>
                <blockquote className="mb-10">
                  <p className="font-headline text-2xl md:text-[2rem] text-haevn-navy leading-[1.2] max-w-3xl" style={{ fontWeight: 400 }}>
                    "I built HAEVN to replace attention with alignment."
                  </p>
                </blockquote>
              </FadeIn>
              <FadeIn delay={200}>
                <h3 className="font-headline text-2xl md:text-3xl text-gray-900 mb-6" style={{ fontWeight: 500 }}>Why I built HAEVN</h3>
                <div className="space-y-5 mb-10">
                  <p className="text-base text-gray-600 font-body leading-relaxed">
                    I met my wife on Match.com nearly twenty years ago, back when meeting online still raised eyebrows.
                  </p>
                  <p className="text-base text-gray-600 font-body leading-relaxed">
                    I was a single dad and a busy professional. Online dating felt like the most efficient way to meet people. It worked. I met the love of my life.
                  </p>
                  <p className="text-base text-gray-600 font-body leading-relaxed">
                    I've always believed in what technology could do for connection. But somewhere along the way, dating apps lost their way.
                  </p>
                  <p className="text-base text-gray-600 font-body leading-relaxed">
                    Swiping replaced substance. Ghosting replaced curiosity. What was meant to bring people together started keeping them apart.
                  </p>
                  <p className="text-base text-gray-600 font-body leading-relaxed">
                    I've always noticed how systems exclude people. By race. By sexuality. By relationship style.
                  </p>
                  <p className="text-base text-gray-600 font-body leading-relaxed">
                    I've never been comfortable accepting "that's just how it is."
                  </p>
                  <p className="text-base text-gray-600 font-body leading-relaxed">
                    So I built HAEVN to offer something better. A space for people to meet with intention and honesty, no matter how they love or what they're looking for.
                  </p>
                </div>
                <div>
                  <img
                    src="https://customer-assets.emergentagent.com/job_2ce4513b-5c90-4107-90a4-2290c44a379e/artifacts/5m10wg1n_Rik-Sig-1.gif"
                    alt="Rik Foote signature"
                    className="h-14 md:h-16 w-auto mb-3"
                  />
                  <p className="text-base text-gray-900 font-body" style={{ fontWeight: 500 }}>Rik Foote</p>
                  <p className="text-sm text-gray-500 font-body">Founder</p>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* 6. CLOSING BRAND SECTION */}
      <section className="px-6 md:px-12 py-14 md:py-20 bg-haevn-navy" data-testid="about-closing">
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <h2 className="font-headline text-3xl md:text-5xl text-white leading-tight mb-4">
              Less noise. More signal.
            </h2>
            <p className="text-lg text-white/60 font-body">
              That's the idea behind HAEVN.
            </p>
          </FadeIn>
        </div>
      </section>

      <style>{`
        @keyframes aboutPanelIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <Footer />
    </div>
  );
}
